import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Loader2, ExternalLink, AlertTriangle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { getBackendUrl } from './utils/api';

const API = `${getBackendUrl()}/api`;

export default function CardOnlineSettings({ token, embedded = false }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPK, setShowPK] = useState(false);
  const [pkInput, setPkInput] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mercadopago/card-settings`, { headers });
      setSettings(r.data);
    } catch (e) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateField = async (field, value) => {
    setSaving(true);
    try {
      await axios.put(`${API}/mercadopago/card-settings`, { [field]: value }, { headers });
      toast.success('Configuração salva');
      load();
    } catch (e) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const savePublicKey = async () => {
    if (!pkInput.trim()) {
      toast.error('Cole a Public Key do Mercado Pago');
      return;
    }
    if (!pkInput.startsWith('APP_USR-') && !pkInput.startsWith('TEST-')) {
      if (!window.confirm('A chave não começa com APP_USR- ou TEST-. Continuar mesmo assim?')) return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/mercadopago/card-settings`, { mp_public_key: pkInput }, { headers });
      toast.success('Public Key salva');
      setPkInput('');
      load();
    } catch (e) {
      toast.error('Erro ao salvar Public Key');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;
  }

  const enabled = settings.card_online_enabled;
  const credentialsOk = settings.has_access_token && settings.has_public_key;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-blue-400" />
            Cartão Online (Mercado Pago)
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Aceite cartão de crédito e débito direto no cardápio digital
          </p>
        </div>
      </div>

      {/* Status credenciais */}
      <div className={`p-4 rounded-xl border ${credentialsOk ? 'bg-green-500/5 border-green-500/30' : 'bg-yellow-500/5 border-yellow-500/30'}`}>
        <div className="flex items-start gap-3">
          {credentialsOk ? (
            <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
          )}
          <div className="flex-1">
            <div className={`font-semibold ${credentialsOk ? 'text-green-300' : 'text-yellow-300'}`}>
              {credentialsOk ? 'Credenciais configuradas' : 'Configure suas credenciais Mercado Pago'}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Access Token: {settings.has_access_token ? '✅ Configurado (mesmo do PIX)' : '❌ Configure na aba "Mercado Pago"'}
              <br />
              Public Key: {settings.has_public_key
                ? <>✅ <span className="font-mono">{settings.mp_public_key_masked}</span></>
                : '❌ Pendente'}
            </div>
            {!settings.has_public_key && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-gray-300">
                  Cole sua <strong>Public Key</strong> do Mercado Pago (não é a mesma do Access Token).
                  Você obtém em:{' '}
                  <a
                    href="https://www.mercadopago.com.br/developers/panel/app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline inline-flex items-center gap-1"
                  >
                    Painel do Desenvolvedor <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
                <div className="flex gap-2">
                  <input
                    type={showPK ? 'text' : 'password'}
                    value={pkInput}
                    onChange={(e) => setPkInput(e.target.value)}
                    placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    data-testid="pk-input"
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPK(!showPK)}
                    className="px-3 bg-white/5 hover:bg-white/10 rounded-lg text-gray-300"
                  >
                    {showPK ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={savePublicKey}
                    disabled={saving}
                    data-testid="pk-save"
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toggle principal */}
      {!embedded && (
        <div className="glass-card p-5 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white">Aceitar pagamento por cartão online</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {enabled
                ? 'Ativo: clientes podem pagar com cartão direto no cardápio'
                : credentialsOk
                  ? 'Ative para mostrar opção de cartão no checkout'
                  : 'Configure as credenciais primeiro'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => updateField('card_online_enabled', !enabled)}
            disabled={saving || !credentialsOk}
            data-testid="card-online-toggle"
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition disabled:opacity-50 ${
              enabled ? 'bg-blue-500' : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      )}

      {enabled && (
        <>
          {/* Crédito / Débito */}
          <div className="glass-card p-5 space-y-4">
            <h3 className="font-semibold text-white">Tipos de cartão aceitos</h3>
            {[
              { key: 'card_online_credit', label: 'Cartão de crédito', desc: 'Aceitar Visa, Master, Elo, Hiper, Amex' },
              { key: 'card_online_debit', label: 'Cartão de débito', desc: 'Aceitar débito (sem parcelamento)' },
            ].map((item) => (
              <label key={item.key} className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-medium text-white">{item.label}</div>
                  <div className="text-xs text-gray-500">{item.desc}</div>
                </div>
                <button
                  type="button"
                  onClick={() => updateField(item.key, !settings[item.key])}
                  disabled={saving}
                  data-testid={`toggle-${item.key}`}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                    settings[item.key] ? 'bg-blue-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      settings[item.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            ))}
          </div>

          {/* Parcelamento */}
          {settings.card_online_credit && (
            <div className="glass-card p-5 space-y-4">
              <h3 className="font-semibold text-white">Parcelamento</h3>

              <div>
                <label className="text-sm text-gray-300 mb-1 block">Máximo de parcelas no crédito</label>
                <select
                  value={settings.card_online_max_installments || 1}
                  onChange={(e) => updateField('card_online_max_installments', parseInt(e.target.value))}
                  disabled={saving}
                  data-testid="max-installments"
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                    <option key={n} value={n} className="bg-gray-800">
                      {n === 1 ? 'Sem parcelamento (1× à vista)' : `Até ${n}×`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-300 mb-2 block">Quem paga os juros do parcelamento?</label>
                <div className="space-y-2">
                  {[
                    {
                      v: 'store',
                      label: 'A loja absorve os juros',
                      desc: 'Cliente vê só "3× R$ 10 sem juros". Você recebe menos. Limita parcelamento até onde MP oferece sem juros.',
                    },
                    {
                      v: 'customer',
                      label: 'O cliente paga os juros',
                      desc: 'Cliente vê "3× R$ 10,50 (total R$ 31,50)". Você recebe valor cheio. Cliente paga juros do MP.',
                    },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      className={`block p-3 rounded-lg cursor-pointer border transition ${
                        settings.card_online_interest_payer === opt.v
                          ? 'bg-blue-500/10 border-blue-500/50'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="interest_payer"
                          value={opt.v}
                          checked={settings.card_online_interest_payer === opt.v}
                          onChange={() => updateField('card_online_interest_payer', opt.v)}
                          disabled={saving}
                          data-testid={`interest-${opt.v}`}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-white text-sm">{opt.label}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{opt.desc}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Aviso de taxas */}
          <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg text-xs text-gray-300 space-y-1">
            <div className="font-semibold text-blue-300 mb-1">💡 Boas práticas</div>
            <p>
              • Taxas Mercado Pago aproximadas: crédito 1× ~4,98% (recebimento 30 dias) — débito ~1,99%
            </p>
            <p>
              • Você define o prazo de recebimento (D+1, D+14, D+30) direto no painel do Mercado Pago
            </p>
            <p>
              • Quando você cancelar um pedido pago, o estorno é automático
            </p>
          </div>
        </>
      )}
    </div>
  );
}
